import React, { useEffect, useState } from 'react'
import { explorerUrl } from '../solana/kit/index.js'
import { solanaCluster, clusterRpcUrl } from '../solana/endpoints.js'
import { anchorDetail, anchorHeadline, checkStatusText, formatDate, needsCountdown } from './anchorText.js'
import { panelRows, panelWarning } from './panelModel.js'
import { fingerprintForWidth } from './panelLayout.js'
import { color, font, size, space, weight } from './verificationTheme'
import type { AnchorState, ChainRecord } from './useChainRecord.js'
import type { ContainerRecord, VersionConsistency } from './useContainerRecord.js'
import type { VerifyState } from './useVideoVerification.js'

// Where a sceptical viewer goes after the chart has convinced them something
// is happening: what the chain actually holds, and how to go and look at it
// without taking this page's word for any of it.
//
// EVERY DECISION IN HERE IS SOMEWHERE ELSE. The sentences come from
// anchorText.ts and the version list from the UNCHANGED panelModel.ts, both of
// which have real tests; this file is layout. That split is deliberate — see
// the header of panelModel.ts for why a .tsx file in this repo cannot be
// tested and therefore must not decide anything.
//
// The two axes never blur. The anchor axis is a promise about what was
// published; the check axis is a statement about bytes that arrived. They are
// drawn as separate lines and neither is ever phrased as the other.

export interface ProvenanceProps {
  verify: VerifyState
  anchor: AnchorState
  chain: ChainRecord | null
  /** The Video account this video's root hash lives on. */
  videoAddress: string | null
  container: ContainerRecord | null
  consistency: VersionConsistency
  playingVideoId: string
  deletedVideoIds?: string[]
  /** How many slices have been checked, for the check-axis line only. */
  checkedCount: number
  /** A phone-width panel: fingerprints and account addresses shorten. */
  narrow?: boolean
  /**
   * Which half to draw. The panel shows the `summary` — the sentences, and
   * any warning, which must never be folded away — and keeps the `record` —
   * fingerprints, addresses, the version list — behind its details switch,
   * for the viewer who wants to go and look for themselves.
   */
  part: 'summary' | 'record'
}

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ opacity: 0.55, paddingRight: space.md }}>{children}</div>
)

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ fontFamily: font.mono, fontSize: size.mono, wordBreak: 'break-all' }}>{children}</span>
)

const linkStyle: React.CSSProperties = {
  color: color.bone,
  textDecorationColor: color.rule,
  textUnderlineOffset: '3px',
}

export default function Provenance({
  verify,
  anchor,
  chain,
  videoAddress,
  container,
  consistency,
  playingVideoId,
  deletedVideoIds,
  checkedCount,
  narrow = false,
  part,
}: ProvenanceProps) {
  // Ticks only while a correction window is open, and at one second because
  // the text is minute-grained — ticking faster buys nothing, ticking not at
  // all freezes "closes in 6 days, 23 hours" at page load.
  const [nowMs, setNowMs] = useState(() => Date.now())
  const ticking = needsCountdown(anchor, chain)
  useEffect(() => {
    if (!ticking) return undefined
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [ticking])

  // The playing version's root hash is the only hash this page has actually
  // read off the chain, so it is the only one a row may print.
  const rootHashes = chain?.rootHash ? { [playingVideoId]: chain.rootHash } : {}
  const rows = panelRows(container, playingVideoId, deletedVideoIds ?? [], rootHashes)
  const warning = panelWarning(consistency)
  const explorer = (id: string): string => explorerUrl(solanaCluster, 'address', id, clusterRpcUrl())

  if (part === 'summary') {
    return (
      <div style={{ fontFamily: font.prose, fontSize: size.body, lineHeight: 1.55 }}>
        <div style={{ fontSize: size.title, fontWeight: weight.semibold, marginBottom: space.xs }}>
          {anchorHeadline(anchor, chain)}
        </div>
        <div style={{ opacity: 0.75, maxWidth: '68ch' }}>{anchorDetail(anchor, chain, nowMs)}</div>
        <div style={{ opacity: 0.75, maxWidth: '68ch' }}>{checkStatusText(verify, checkedCount)}</div>

        {warning && (
          <div
            style={{
              marginTop: space.md,
              paddingLeft: space.md,
              borderLeft: `2px solid ${color.rule}`,
              maxWidth: '68ch',
            }}
          >
            <div style={{ fontWeight: weight.semibold }}>{warning.title}</div>
            <div style={{ opacity: 0.75 }}>{warning.detail}</div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ fontFamily: font.prose, fontSize: size.body, lineHeight: 1.55 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? '1fr' : 'max-content 1fr',
          rowGap: narrow ? 2 : space.xs,
          alignItems: 'baseline',
        }}
      >
        {chain && (
          <>
            <Label>Sealed fingerprint</Label>
            <Mono>{fingerprintForWidth(chain.rootHash, narrow)}</Mono>

            <Label>Published</Label>
            <div>
              {formatDate(chain.publishedAt)}
              <span style={{ opacity: 0.55 }}> · revision {chain.revision}</span>
            </div>
          </>
        )}

        {videoAddress && (
          <>
            <Label>This video on chain</Label>
            <div>
              <a href={explorer(videoAddress)} target="_blank" rel="noreferrer" style={linkStyle}>
                <Mono>{fingerprintForWidth(videoAddress, narrow)}</Mono>
              </a>
            </div>
          </>
        )}

        {container && (
          <>
            <Label>Its version history</Label>
            <div>
              <a href={explorer(container.containerPda)} target="_blank" rel="noreferrer" style={linkStyle}>
                <Mono>{fingerprintForWidth(container.containerPda, narrow)}</Mono>
              </a>
            </div>
          </>
        )}
      </div>

      {rows.length > 0 && (
        <div style={{ marginTop: space.md }}>
          <div style={{ opacity: 0.55, marginBottom: space.xs }}>Versions the chain lists</div>
          {rows.map((row) => (
            <div
              key={row.videoId}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: space.sm,
                padding: `${space.xs}px 0`,
                borderTop: `1px solid ${color.rule}`,
                opacity: row.deleted ? 0.5 : 1,
              }}
            >
              <div style={{ minWidth: 34, fontWeight: row.watching ? weight.semibold : weight.regular }}>
                {row.label}
              </div>
              <div style={{ flex: 1 }}>
                <a href={explorer(row.videoPda)} target="_blank" rel="noreferrer" style={linkStyle}>
                  <Mono>{fingerprintForWidth(row.videoPda, narrow)}</Mono>
                </a>
              </div>
              <div style={{ opacity: 0.55, whiteSpace: 'nowrap' }}>
                {row.watching ? 'watching now' : row.current ? 'current' : formatDate(row.addedAt)}
                {row.deleted ? ' · removed' : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
